import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { Reveal, SectionHeading, GroundEdge, Sprout, FloatingDecor, PageMeta } from "./ThtParts";
import "./ThtTop.css";
import "./ThtDesign.css";
import "./ThtBusiness.css";
import "./ThtNatural.css";

// 写真が届くまでの仮表示(葉の形の枠)
// 写真(src がないものは、届くまでの仮表示)
const Photo = ({ label, src, className = "" }: { label: string; src?: string; className?: string }) => (
  <div className={`biz-photo ${className}${src ? " has-img" : ""}`}>
    {src ? <img src={src} alt={label} loading="lazy" /> : <span>{label}</span>}
  </div>
);

const sections = [
  { id: "sports", title: "スポーツクラブ", en: "Sports Club", color: "#5cae5a" },
  { id: "mtt", title: "MTT大会・進級テスト", en: "Competition", color: "#f2b632" },
  { id: "juku", title: "学習塾", en: "Learning", color: "#4a90c8" },
];

// 写真が用意できた種目だけ src を指定する
const sportsCategories: { name: string; src?: string }[] = [
  { name: "体操", src: "/images/gymnastics.png" },
  { name: "体幹", src: "/images/core.jpg" },
  { name: "陸上", src: "/images/track-and-field.jpg" },
  { name: "チアリーディング", src: "/images/cheerleading.jpg" },
];

// MTT大会のキャラクターと吹き出しのセリフ(大会・進級テストで子どもたちが口にしそうな言葉)
const mttCharacters = [
  { color: "green", hex: "#1a8a3c", lines: ["できた！", "見ててね！", "もう1回！"] },
  { color: "blue", hex: "#4a8ac8", lines: ["がんばれー！", "ナイス！", "いけるよ！"] },
  { color: "pink", hex: "#b05a9e", lines: ["次の級へ！", "合格したよ！", "目標きめた！"] },
  { color: "yellow", hex: "#f2b632", lines: ["たのしい！", "ドキドキ…", "みんなで挑戦！"] },
];

const SHOW_MS = 2800; // 吹き出しを表示している時間
const CYCLE_MS = 4400; // 次のセリフまでの間隔(表示 + 消えている時間)

// 1体分:数秒話して消え、少し間をあけて別のセリフを話す(4体で話し出すタイミングをずらす)
const MttCharacter = ({ c, i }: { c: (typeof mttCharacters)[number]; i: number }) => {
  const [line, setLine] = useState(0);
  const [talking, setTalking] = useState(false);

  useEffect(() => {
    // セリフも話すタイミングも毎回ランダムにして、決まった順番に見えないようにする
    let timer: ReturnType<typeof setTimeout>;
    let prev = -1;
    const random = (min: number, max: number) => min + Math.random() * (max - min);
    const speak = () => {
      let next = Math.floor(Math.random() * c.lines.length);
      if (next === prev) next = (next + 1) % c.lines.length; // 同じセリフが続かないように
      prev = next;
      setLine(next);
      setTalking(true);
      timer = setTimeout(() => {
        setTalking(false);
        // 次に話すまでの間もランダム
        timer = setTimeout(speak, random(CYCLE_MS - SHOW_MS, CYCLE_MS * 1.6));
      }, SHOW_MS);
    };
    timer = setTimeout(speak, random(800, 4000));
    return () => clearTimeout(timer);
  }, [c.lines.length]);

  return (
    <div className={`biz-mtt-char biz-mtt-char-${i + 1}`} style={{ "--char": c.hex } as React.CSSProperties}>
      <AnimatePresence mode="wait">
        {talking && (
          <motion.span
            key={line}
            className="biz-mtt-bubble"
            initial={{ opacity: 0, scale: 0.6, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -6 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            {c.lines[line]}
          </motion.span>
        )}
      </AnimatePresence>
      <motion.img
        src={`/images/mttcharacter-${c.color}.png`}
        alt=""
        loading="lazy"
        initial={{ opacity: 0, y: 40, scale: 0.6 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.12 * i }}
      />
    </div>
  );
};

const MttCharacters = () => (
  <div className="biz-mtt-chars" aria-hidden="true">
    {mttCharacters.map((c, i) => (
      <MttCharacter key={c.color} c={c} i={i} />
    ))}
  </div>
);

const jukuFeatures = [
  { title: "天神による個別学習", text: "一人ひとりの理解度に合わせた問題で、自分のペースで学習を進めます。" },
  { title: "さかのぼり学習", text: "つまずきの原因まで前の学年にさかのぼって復習し、「わからない」を根本から解消します。" },
  { title: "ほめて伸ばすサポート", text: "講師がそばで見守り、できたことをしっかりほめて、次の目標を一緒に決めます。" },
];

// ブラウザのタブに表示するページ名と、検索結果に出る説明
const pageMeta = {
  title: "業務内容｜THT合同会社",
  description: "スポーツクラブ、MTT大会・進級テスト、学習塾。THT合同会社の事業をご紹介します。",
  canonical: "https://tht-group.co.jp/business",
};

const ThtBusiness = () => {
  const { hash } = useLocation();
  // ホームの事業(#sports など)から来たときは、最初からその事業の位置を表示する
  useEffect(() => {
    if (!hash) return;
    const timer = setTimeout(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" });
    }, 0);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
  <>
    <PageMeta {...pageMeta} />
    <MotionConfig reducedMotion="user">
    <div className="tht-home biz">
      {/* ページの見出し */}
      <section className="biz-hero">
        <FloatingDecor />
        <div className="content-container">
          <SectionHeading en="Business" ja="業務内容" />
          <Reveal delay={0.1}>
            <p className="lead-text">
              体を動かすことも、学ぶことも、同じ「成長のタネ」。
              <br className="pc-only" />
              THT合同会社は、子どもたちがそれぞれのペースで芽を出せる場所を、3つの事業で地域につくっています。
            </p>
          </Reveal>
          {/* 3事業へのページ内リンク(並列なので番号は付けない) */}
          <Reveal delay={0.2}>
            <nav className="biz-anchors">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  style={{ "--accent": s.color } as React.CSSProperties}
                  onClick={(e) => {
                    // 一瞬で飛ばず、なめらかにスクロールして移動する
                    e.preventDefault();
                    document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </Reveal>
        </div>
      </section>

      {/* スポーツクラブ:4種目を写真タイルで見せる */}
      <section id="sports" className="biz-section" style={{ "--accent": sections[0].color } as React.CSSProperties}>
        <div className="content-container">
          <Reveal className="biz-title" x={-48} y={0}>
            <span className="biz-title-en">{sections[0].en}</span>
            <h2>スポーツクラブ</h2>
          </Reveal>
          <div className="biz-split biz-split-single">
            <Reveal className="biz-text" x={-48} y={0}>
              <h3 className="biz-catch">ほめて伸ばし「できた」で好奇心をさかせる</h3>
              <p>
                得意・不得意に関係なく、誰もが自分のペースで体を動かせるスポーツクラブです。
                各年齢の子が集まり、将来必要な動きや学校体育で行う種目（跳び箱・鉄棒・マット・トランポリンなど）に取り組みます。
              </p>
              <p>
                できたことを見逃さずにほめ、小さな「できた」を積み重ねます。
                その喜びが「もっとやってみたい」という好奇心を咲かせ、運動以外の挑戦にもつながっていきます。
              </p>
            </Reveal>
          </div>
          <div className="biz-tiles">
            {sportsCategories.map(({ name: c, src }, i) => (
              <Reveal key={c} delay={0.08 * i}>
                <div className="biz-tile">
                  <Photo label={`${c}の写真`} src={src} />
                  <span className="biz-tile-label">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="biz-brands">
            <span>運営ブランド</span>
            <p>土日だけ体操教室 ／ ココナスポーツクラブ</p>
          </Reveal>
          {/* 通わせたいと思った人が、すぐ教室探し・無料体験へ進めるように */}
          <Reveal className="btn-row biz-class-link">
            <Link to="/class" className="tht-btn btn-orange">
              教室一覧・無料体験はこちら <span>&rarr;</span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* MTT大会・進級テスト:写真と流れで「挑戦の場」を見せる */}
      <section id="mtt" className="biz-section biz-section-tint" style={{ "--accent": sections[1].color } as React.CSSProperties}>
        <GroundEdge color="#fdf8ec" />
        <div className="biz-section-body">
          <FloatingDecor />
          <div className="content-container">
            <Reveal className="biz-title" x={48} y={0}>
              <span className="biz-title-en">{sections[1].en}</span>
              <h2>MTT大会・進級テスト</h2>
            </Reveal>
            <div className="biz-split biz-split-reverse">
              <Reveal className="biz-text" x={48} y={0}>
                <h3 className="biz-catch">がんばりを形にして、次の「できた」へつなげる</h3>
                <p>
                  日ごろの練習の成果を確かめ、発揮する場所です。
                  進級テストで成長を目に見える形にし、大会では仲間と挑戦し、応援し合います。
                </p>
                <p>結果だけでなく、そこまでの挑戦をしっかりほめ、次の目標へ向かう好奇心を育てます。</p>
              </Reveal>
              <Reveal x={-48} y={0}>
                <Photo label="MTT大会の表彰の様子" src="/images/mtt-tournament.jpg" className="biz-photo-large" />
              </Reveal>
            </div>
            {/* 進級テストと大会の役割の違い(順番ではなく2つの場) */}
            <div className="biz-pair">
              <Reveal className="biz-pair-item">
                <h4>進級テスト</h4>
                <p>日ごろの練習の成果を確かめ、成長を目に見える形にします。次の目標が見えてきます。</p>
              </Reveal>
              <Reveal className="biz-pair-item" delay={0.1}>
                <h4>MTT大会</h4>
                <p>200名以上が参加する大型の大会イベント。仲間と挑戦し、応援し合う場です。</p>
              </Reveal>
            </div>
            {/* MTT大会のキャラクター:左右に散らばって、順番に話しかける */}
            <MttCharacters />
          </div>
        </div>
      </section>

      {/* 学習塾:3つの特長をリストで */}
      <section id="juku" className="biz-section" style={{ "--accent": sections[2].color } as React.CSSProperties}>
        <FloatingDecor />
        <div className="content-container">
          <Reveal className="biz-title" x={-48} y={0}>
            <span className="biz-title-en">{sections[2].en}</span>
            <h2>学習塾</h2>
          </Reveal>
          <Reveal className="biz-tenjin">
            <img src="/images/tenjin-logo.jpg" alt="デジタル教材「天神」 Multimedia Learning System TENJIN" loading="lazy" />
          </Reveal>
          <Reveal className="biz-text biz-text-center">
            <h3 className="biz-catch">「わかった」を積み重ねて、学ぶ楽しさを咲かせる</h3>
            <p>
              「わからない」を、そのままにしない学習塾です。デジタル教材「天神」を使い、
              一人ひとりの理解度に合わせて、つまずいたところから学び直します。
              小さな「わかった」「できた」をほめて積み重ね、自分から学ぶ力と好奇心を育てます。
            </p>
          </Reveal>
          <ul className="biz-features">
            {jukuFeatures.map((f, i) => (
              <Reveal key={f.title} delay={0.1 * i}>
                <li>
                  <Sprout className="biz-feature-icon" />
                  <div>
                    <h4>{f.title}</h4>
                    <p>{f.text}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* 次の行動:控えめな導線 */}
      <section className="biz-next">
        <Reveal className="content-container">
          <p>教室の見学・体験や、事業についてのご相談はお気軽にどうぞ。</p>
          <Link to="/contact" className="tht-btn btn-orange">
            お問い合わせ <span>&rarr;</span>
          </Link>
        </Reveal>
      </section>
    </div>
  </MotionConfig>
    </>
);

};

export default ThtBusiness;
